import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AgentCardView } from "@/components/chat/AgentCardView";
import { AgentMarkdown } from "@/components/chat/AgentMarkdown";
import { ProgressText } from "@/components/chat/AgentProgressText";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { WebSearchToggle } from "@/components/chat/WebSearchToggle";
import { Button } from "@/components/design/Button";
import type { AgentProgress } from "@/lib/agent-agui";
import type { NotebookFile } from "./StudioRecordEntryCard";
import { downloadBlob } from "@/lib/file-download";
import { useAgentStore, type AgentCard, type AgentMessage } from "@/stores/agent-store";
import {
  documentToMarkdown,
  escapeMarkers,
  notebookFileName,
  notebookSegments,
  stripPassageIds,
  unfenceMarkdown,
  exportMarkdown,
  type StudioAsk,
  type StudioCard,
  type StudioNotebook,
} from "./notebook-markdown";
import {
  ThoughtChain,
  ThoughtChainContent,
  ThoughtChainItem,
  ThoughtChainStep,
  ThoughtChainTrigger,
} from "./ThoughtChain";
import { StudioSearchSources } from "./StudioSearchSources";
import { CountdownRing } from "./CountdownRing";
import { StudioToolPills, type StudioToolAction } from "./StudioToolPills";
import { PersonChoiceContext, personChosenMessage, type PersonChoice } from "@/components/chat/person-choice";
import { Paperclip } from "lucide-react";
import { StudioDocumentCard } from "./StudioDocumentCard";
import { StudioDocumentPicker } from "./StudioDocumentPicker";

const NO_MESSAGES: AgentMessage[] = [];

// How long a second Enter turns the note just filed into a question.
const ASK_INSTEAD_MS = 30_000;

// A card as the notebook keeps it: its kind and what it holds, without the chat's bookkeeping.
const toStudioCard = (card: AgentCard): StudioCard =>
  card.kind === "studioProposal" ? { kind: card.kind, data: card.data } : { kind: card.kind, data: card.data };

// The page scrolls in the shell's content area, not in a box of its own: the nearest ancestor that scrolls.
function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
}

// Within this distance of the end the reader counts as following along, as in the drawer's useStickToBottom.
const FOLLOW_THRESHOLD_PX = 48;

interface Turn {
  question: AgentMessage;
  answer?: AgentMessage;
}

function turnsOf(messages: AgentMessage[]): Turn[] {
  const turns: Turn[] = [];
  for (const message of messages) {
    if (message.role === "user") turns.push({ question: message });
    else if (turns.length && !turns[turns.length - 1]!.answer) turns[turns.length - 1]!.answer = message;
  }
  return turns;
}

// One prompt asked in the notebook, kept in it or still running: its working steps as items, then the
// answer and any card.
function AskStep({ question, answer, error, cards, steps, running, onExport, onCardChange, notebookFile }: {
  question: string;
  answer?: string;
  error?: string;
  cards: AgentCard[];
  steps: AgentProgress[];
  running: boolean;
  // Set for the notebook's final version: its answer is the file to hand on.
  onExport?: () => void;
  // Set once the prompt is kept in the notebook: its cards can then be edited there.
  onCardChange?: (index: number, card: StudioCard) => void;
  notebookFile?: NotebookFile;
}) {
  return (
    <ThoughtChainStep status={running ? "active" : "done"}>
      <ThoughtChainTrigger>{question}</ThoughtChainTrigger>
      <ThoughtChainContent>
        {steps.map((step, index) => (
          <ThoughtChainItem key={index}><ProgressText progress={step} /></ThoughtChainItem>
        ))}
        {running && !steps.length ? <ThoughtChainItem><ProgressText progress={null} /></ThoughtChainItem> : null}
        {answer ? (
          <div className="mt-1 text-smycles-body leading-relaxed text-ink">
            <AgentMarkdown content={stripPassageIds(answer)} streaming={running} />
          </div>
        ) : null}
        {error ? <p className="mt-1 text-smycles-meta text-ink">{error}</p> : null}
        {cards.map((card, index) => (
          <AgentCardView key={card.id} card={card} notebookFile={notebookFile} onChange={onCardChange ? (next) => onCardChange(index, next) : undefined} />
        ))}
        {onExport && answer && !running ? (
          <div className="mt-2">
            <Button id="studio-export-final-file" variant="secondary" size="compact" onClick={onExport}>
              <FormattedMessage id="studio.exportFinalFile" defaultMessage="Export as file" />
            </Button>
          </div>
        ) : null}
      </ThoughtChainContent>
    </ThoughtChainStep>
  );
}

// Everything the user wrote between two prompts is one note: one card, read as Markdown and edited as a whole.
function NoteCard({ text, disabled, onSave }: { text: string; disabled: boolean; onSave: (next: string) => Promise<boolean> }) {
  const intl = useIntl();
  const [draft, setDraft] = useState<string | null>(null);
  if (draft !== null) {
    return (
      <form className="box flex flex-col gap-2 bg-surface p-4" onSubmit={async (event) => {
        event.preventDefault();
        if (await onSave(draft)) setDraft(null);
      }}>
        <textarea
          autoFocus
          rows={Math.min(16, Math.max(3, draft.split("\n").length + 1))}
          value={draft}
          aria-label={intl.formatMessage({ id: "studio.noteEdit", defaultMessage: "Edit note" })}
          onChange={(event) => setDraft(event.target.value)}
          className="smycles-field-value w-full resize-y rounded-md bg-page p-3 ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="compact" onClick={() => setDraft(null)}><FormattedMessage id="common.cancel" defaultMessage="Cancel" /></Button>
          <Button type="submit" variant="primary" size="compact" disabled={disabled || !draft.trim() || draft.trim() === text}>
            <FormattedMessage id="common.save" defaultMessage="Save" />
          </Button>
        </div>
      </form>
    );
  }
  return (
    <div className="box group relative bg-surface p-4 pr-12">
      <div className="text-smycles-body leading-relaxed text-ink"><AgentMarkdown content={text} /></div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setDraft(text)}
        aria-label={intl.formatMessage({ id: "studio.noteEdit", defaultMessage: "Edit note" })}
        className="absolute right-3 top-3 rounded p-1 text-ink-muted opacity-0 transition-opacity hover:text-ink focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui group-hover:opacity-100 disabled:hidden [@media(hover:none)]:opacity-100"
      >
        <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </button>
    </div>
  );
}

/**
 * The open notebook in the order things came: the user's text, and between it every prompt asked here as a
 * step of a chain. A finished prompt is written into the notebook where it was asked, so the view is
 * the same after a reload; one still running shows from the store. The turn goes through the always-mounted
 * assistant (it sends whether its drawer is open or not).
 */
export function StudioNotebookStream({ book, writable, onAppend, onUndoAppend, onKeepAsk, onEditNote, onEditCard, aiActions = [], notebookActions = [] }: {
  book: StudioNotebook;
  writable: boolean;
  onAppend: (text: string) => Promise<boolean>;
  onUndoAppend: (text: string) => Promise<boolean>;
  onKeepAsk: (ask: StudioAsk) => Promise<boolean>;
  onEditNote: (segment: number, expected: string, text: string) => Promise<boolean>;
  onEditCard: (askId: string, cardIndex: number, card: StudioCard) => Promise<boolean>;
  // The further assistant actions behind the "+" beside the ask button.
  aiActions?: StudioToolAction[];
  // What the notebook itself offers (edit the text, export): after the assistant's actions, and no question follows.
  notebookActions?: StudioToolAction[];
}) {
  const intl = useIntl();
  const messages = useAgentStore((s) => (s.studioConversationFor === book.id ? s.messages : NO_MESSAGES));
  const pending = useAgentStore((s) => s.pending);
  const progress = useAgentStore((s) => s.progress);
  const askInNotebook = useAgentStore((s) => s.askInNotebook);
  const [draft, setDraft] = useState("");
  const segments = notebookSegments(book.text);
  const kept = new Set(segments.flatMap((segment) => (segment.kind === "ask" ? [segment.id] : [])));
  const turns = turnsOf(messages);
  const live = turns.filter((turn) => !kept.has(turn.question.id));
  const runningId = pending ? turns[turns.length - 1]?.question.id : undefined;

  // A finished prompt goes into the notebook, once; until the notebook has it, it keeps showing from the store.
  const keeping = useRef(new Set<string>());
  useEffect(() => {
    for (const { question, answer } of live) {
      if (question.id === runningId || keeping.current.has(question.id)) continue;
      if (!answer || answer.error || !(answer.content || answer.cards?.length)) continue;
      keeping.current.add(question.id);
      void onKeepAsk({
        id: question.id,
        at: Date.now(),
        question: question.content,
        answer: stripPassageIds(question.studioPurpose === "final" ? unfenceMarkdown(answer.content) : answer.content),
        cards: (answer.cards ?? []).map(toStudioCard),
        steps: answer.steps ?? [],
        ...(question.studioPurpose === "final" ? { final: true } : {}),
      }).then((ok) => {
        if (!ok) keeping.current.delete(question.id);
      });
    }
  }, [live, runningId, onKeepAsk]);

  // Like a chat: a notebook opens at its end, the newest content and the composer in sight, and stays there
  // while anything below grows (a note, an answer coming in, a step unfolding, a card). Watching the size
  // rather than the data catches all of it. A reader who scrolls up to something older is left there until
  // they come back down; adding or asking takes them down again.
  const streamRef = useRef<HTMLElement>(null);
  const following = useRef(true);
  useLayoutEffect(() => {
    const stream = streamRef.current;
    const scroller = stream ? scrollParent(stream) : null;
    if (!stream || !scroller) return;
    // The document's own scrolling reports on window, not on its element.
    const events: HTMLElement | Window = scroller === document.scrollingElement ? window : scroller;
    const pin = () => {
      if (following.current) scroller.scrollTop = scroller.scrollHeight;
    };
    const onScroll = () => {
      following.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < FOLLOW_THRESHOLD_PX;
    };
    pin();
    events.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(pin);
    observer.observe(stream);
    return () => {
      events.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);
  const follow = () => {
    following.current = true;
  };
  // The notebook as a file a draft for an organizational record can take along: readable, without markers.
  const notebookFile: NotebookFile = {
    name: book.name,
    fileName: notebookFileName(book.name),
    markdown: exportMarkdown(book, { withAnswers: true, proposalLabel: intl.formatMessage({ id: "studio.proposalLabel", defaultMessage: "Proposal" }), documentLabel: intl.formatMessage({ id: "studio.documentLabel", defaultMessage: "Document" }) }),
  };
  // Writing is the notebook's normal case, asking the exception: Enter always files a note, and for the next
  // thirty seconds a second Enter asks with it instead (an undo window, not a guess about what the text meant).
  const [pickingDocument, setPickingDocument] = useState(false);
  const [justFiled, setJustFiled] = useState<{ note: string; question: string; filing: number } | null>(null);
  // Counts the notes filed here, so that each window restarts its countdown ring.
  const filings = useRef(0);
  useEffect(() => {
    if (!justFiled) return;
    // Each filing is a new object, so the window starts over with it.
    const timer = setTimeout(() => setJustFiled(null), ASK_INSTEAD_MS);
    return () => clearTimeout(timer);
  }, [justFiled]);
  // The timer above ends the window; while a prompt runs there is nothing to ask with.
  const askInstead = justFiled && !pending ? justFiled : null;
  const canNote = writable && !pending && Boolean(draft.trim());
  const canAsk = !pending && Boolean(draft.trim());
  // Out of the composer at once, as in a chat, and the window opens at once: the save runs in the notebook's
  // write queue, so taking the note back waits for it. Waiting for the save first cleared whatever the user had
  // typed meanwhile and reopened the window over it.
  const addNote = async () => {
    if (!canNote) return;
    follow();
    const typed = draft;
    const note = escapeMarkers(typed);
    filings.current += 1;
    const filed = { note, question: typed.trim(), filing: filings.current };
    setDraft("");
    setJustFiled(filed);
    if (!(await onAppend(note))) {
      setJustFiled((current) => (current === filed ? null : current));
      // Not lost: back into the composer, ahead of anything typed since.
      setDraft((current) => (current.trim() ? `${typed}\n\n${current}` : typed));
    }
  };
  const ask = () => {
    if (!canAsk) return;
    follow();
    setJustFiled(null);
    askInNotebook(book.id, draft.trim());
    setDraft("");
  };
  // The note becomes the question: taken back from the end of the notebook, then asked. Taken back only while it
  // is still the end; the question is kept in the notebook either way.
  const convert = async () => {
    if (!askInstead) return;
    const { note, question } = askInstead;
    setJustFiled(null);
    follow();
    await onUndoAppend(note);
    askInNotebook(book.id, question);
  };
  // Arrow up takes the note just filed back into the composer to correct it, as a chat edits the last message;
  // Enter files it again. Only while it is still the end of the notebook, so nothing is filed twice.
  const reopen = async () => {
    if (!askInstead) return;
    const { note, question } = askInstead;
    setJustFiled(null);
    if (await onUndoAppend(note)) setDraft((current) => (current.trim() ? current : question));
  };
  const enterKey = intl.formatMessage({ id: "studio.shortcut.enter", defaultMessage: "Enter" });
  const modEnterKey = intl.formatMessage({ id: "studio.shortcut.modEnter", defaultMessage: "Cmd/Ctrl + Enter" });
  // The key Enter now triggers, as a key cap: a word on a key reads unambiguously, an arrow also reads as "back".
  const enterKeyCap = (
    <kbd aria-hidden="true" className="ml-1.5 rounded px-1 py-0.5 font-sans text-smycles-micro leading-none ring-1 ring-current/40">
      {intl.formatMessage({ id: "studio.key.enter", defaultMessage: "Enter" })}
    </kbd>
  );
  const exportFinal = (answer: string) => () =>
    downloadBlob(new Blob([`${unfenceMarkdown(answer)}\n`], { type: "text/markdown" }), notebookFileName(book.name).replace(/\.md$/, "-final.md"));

  // Text as articles, prompts as steps; steps that follow each other share one chain, so the line runs on.
  const items: { key: string; step: boolean; node: ReactNode }[] = [
    ...segments.map((segment, index) =>
      segment.kind === "document"
        ? {
            key: `d${index}`,
            step: false,
            node: <StudioDocumentCard key={`d${index}`} document={segment} />,
          }
        : segment.kind === "text"
        ? {
            key: `t${index}`,
            step: false,
            node: (
              <NoteCard
                key={`t${index}`}
                text={segment.text}
                disabled={!writable || pending}
                onSave={(next) => onEditNote(index, segment.text, next)}
              />
            ),
          }
        : {
            key: segment.id,
            step: true,
            node: (
              <AskStep
                key={segment.id}
                question={segment.question}
                answer={segment.answer}
                cards={segment.cards.map((card, at) => ({ ...card, id: `${segment.id}-${at}` }))}
                steps={segment.steps}
                running={false}
                onExport={segment.final ? exportFinal(segment.answer) : undefined}
                onCardChange={(index, card) => void onEditCard(segment.id, index, card)}
                notebookFile={notebookFile}
              />
            ),
          },
    ),
    ...live.map(({ question, answer }) => ({
      key: question.id,
      step: true,
      node: (
        <AskStep
          key={question.id}
          question={question.content}
          answer={answer?.content}
          error={answer?.error}
          cards={answer?.cards ?? []}
          steps={question.id === runningId ? progress : answer?.steps ?? []}
          running={question.id === runningId}
          onExport={question.studioPurpose === "final" && answer?.content ? exportFinal(answer.content) : undefined}
        />
      ),
    })),
  ];
  const groups: { key: string; step: boolean; nodes: ReactNode[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (item.step && last?.step) last.nodes.push(item.node);
    else groups.push({ key: item.key, step: item.step, nodes: [item.node] });
  }

  // A person chosen among a reply's candidates is the user's answer in this notebook, and remembered.
  const choosePerson = (choice: PersonChoice) => {
    if (pending) return;
    useAgentStore.getState().choosePerson(choice.asked, choice.id);
    follow();
    setJustFiled(null);
    askInNotebook(book.id, personChosenMessage(intl, choice));
  };
  return (
    <PersonChoiceContext.Provider value={choosePerson}>
    <section id="studio-stream" ref={streamRef} className="max-w-4xl">
      {groups.length ? groups.map((group) => (
        <div key={group.key} className="mt-6 first:mt-0">
          {group.step ? <ThoughtChain>{group.nodes}</ThoughtChain> : group.nodes}
        </div>
      )) : (
        <p className="py-4 text-smycles-body text-ink-muted">
          <FormattedMessage id="studio.emptyNotebook" defaultMessage="Everything starts with a first note. Add what you have; it does not need to be complete." />
        </p>
      )}
      {/* Pinned to the bottom of the visible area, over a page-coloured band so the text scrolls away behind it. */}
      <div className="sticky bottom-0 z-10 bg-page pb-4 pt-2">
        <form className="box bg-surface p-3" onSubmit={(event) => event.preventDefault()}>
          {/* Over the field: where the question searches on the left, the tools on the right; below it only
              what files or asks (Marvin, 2026-09-29). */}
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <WebSearchToggle id="studio-web-search" disabled={pending} />
              <StudioSearchSources disabled={pending} />
            </div>
            <StudioToolPills
              disabled={pending}
              groups={[
                // Material first: a document the notebook then works with.
                writable ? [{
                  id: "studio-bring-document",
                  label: intl.formatMessage({ id: "studio.document.bring", defaultMessage: "Bring in a document" }),
                  hint: intl.formatMessage({ id: "studio.document.bringHint", defaultMessage: "From your documents, read by OCR, as context" }),
                  icon: Paperclip,
                  onSelect: () => setPickingDocument(true),
                }] : [],
                aiActions.map((action) => ({ ...action, onSelect: () => { follow(); setJustFiled(null); action.onSelect(); } })),
                notebookActions,
              ]}
            />
          </div>
          <textarea
            id="studio-compose"
            rows={3}
            value={draft}
            aria-label={intl.formatMessage({ id: "studio.compose", defaultMessage: "Write a note or ask the assistant" })}
            placeholder={intl.formatMessage({ id: "studio.composePlaceholder", defaultMessage: "Type a note. Enter files it. Within 30 seconds a second Enter asks the assistant with it instead, arrow up takes it back to edit." })}
            onChange={(event) => {
              setDraft(event.target.value);
              // Typing the next note ends the offer to ask with the last one.
              setJustFiled(null);
            }}
            // Enter files a note; a second Enter right after asks with it instead. Shift+Enter breaks the line,
            // Cmd/Ctrl+Enter asks at once.
            onKeyDown={(event) => {
              if (event.key === "ArrowUp" && askInstead && !draft && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void reopen();
                return;
              }
              if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
              event.preventDefault();
              if (event.metaKey || event.ctrlKey) ask();
              else if (askInstead && !draft.trim()) void convert();
              else void addNote();
            }}
            // Grows with the text and scrolls past about ten lines, with no grip: dragging one on a composer pinned to
            // the bottom grows it upward, away from the pointer, so it runs off (the drawer's composer says the same).
            className="smycles-field-value field-sizing-content max-h-56 min-h-22 w-full resize-none rounded-md bg-page p-3 ring-1 ring-ink/10 placeholder:text-ink-muted focus:outline-none focus:ring-ink-ui"
          />
          <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
            {/* Not while a prompt runs: its answer is written where it was asked, and a note added meanwhile
                would end up above it. */}
            {/* Both keep their place: the primary look moves to the ask button for the seconds in which Enter
                would ask with the note just filed, and its ring runs down that time. */}
            <Button
              id="studio-add-note"
              variant={askInstead ? "secondary" : "primary"}
              size="compact"
              disabled={!canNote}
              title={askInstead ? undefined : enterKey}
              aria-keyshortcuts={askInstead ? undefined : "Enter"}
              onClick={() => void addNote()}
            >
              <FormattedMessage id="studio.addToNotebook" defaultMessage="Add to notebook" />
              {askInstead ? null : enterKeyCap}
            </Button>
            <AskAssistantButton
              id="studio-ask"
              variant={askInstead ? "primary" : "secondary"}
              disabled={askInstead ? pending : !canAsk}
              title={askInstead ? intl.formatMessage({ id: "studio.askInsteadHint", defaultMessage: "Enter asks the assistant with the note just filed, arrow up takes it back to edit" }) : modEnterKey}
              keyShortcuts={askInstead ? "Enter" : "Control+Enter Meta+Enter"}
              label={askInstead ? (
                <>
                  <FormattedMessage id="studio.askAssistant" defaultMessage="Ask the assistant" />
                  {enterKeyCap}
                  <CountdownRing key={askInstead.filing} durationMs={ASK_INSTEAD_MS} className="-my-1 ml-1.5" />
                </>
              ) : <FormattedMessage id="studio.askAssistant" defaultMessage="Ask the assistant" />}
              onAsk={askInstead ? () => void convert() : ask}
            />
          </div>
        </form>
      </div>
      {/* Mounted only while open: the user's documents load when reached for, not with every notebook. */}
      {pickingDocument ? (
        <StudioDocumentPicker
          open
          onClose={() => setPickingDocument(false)}
          onBring={async (document) => {
            follow();
            setJustFiled(null);
            return onAppend(documentToMarkdown(document));
          }}
        />
      ) : null}
    </section>
    </PersonChoiceContext.Provider>
  );
}
